import React, { useState, useMemo } from "react";
import AppLayout from "../components/AppLayout";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
    PieChart, Pie, Cell, Legend,
} from "recharts";
import { Calculator as CalcIcon, Landmark, TrendingUp, Home, Percent } from "lucide-react";
import { motion } from "framer-motion";
import CountUp from "../components/CountUp";
import SliderInput from "../components/SliderInput";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n || 0);
const fmt0 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const CALC_TABS = [
    { key: "loan", icon: Landmark, label_es: "Préstamo", label_en: "Loan" },
    { key: "investment", icon: TrendingUp, label_es: "Inversión", label_en: "Investment" },
    { key: "compound", icon: Percent, label_es: "Interés compuesto", label_en: "Compound Interest" },
    { key: "mortgage", icon: Home, label_es: "Hipoteca", label_en: "Mortgage" },
];

const ResultTile = ({ label, value, color = "text-[hsl(var(--foreground))]", big = false, numeric = null, format = null }) => (
    <div>
        <div className="text-[10px] font-bold tracking-[0.2em] text-[hsl(var(--muted-foreground))] uppercase">{label}</div>
        <div className={`font-display font-extrabold tabular-nums ${big ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"} tracking-tight ${color} mt-1`}>
            {numeric !== null ? <CountUp value={numeric} format={format || ((n) => n.toFixed(0))} /> : value}
        </div>
    </div>
);

// (AnimatedChart helper removed — animations are applied inline per chart)

// ---------- Loan Calculator ----------
const LoanCalc = ({ language }) => {
    const [principal, setPrincipal] = useState(20000);
    const [rate, setRate] = useState(6.5);
    const [years, setYears] = useState(5);

    const result = useMemo(() => {
        const monthlyRate = rate / 100 / 12;
        const n = years * 12;
        const monthlyPayment = monthlyRate === 0
            ? principal / n
            : (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
        const totalPaid = monthlyPayment * n;
        const totalInterest = totalPaid - principal;

        const series = [];
        let balance = principal;
        for (let i = 0; i <= n; i++) {
            series.push({ month: i, balance: Math.max(0, balance), interest: totalPaid - balance - (n - i) * monthlyPayment > 0 ? 0 : 0 });
            const interestPayment = balance * monthlyRate;
            const principalPayment = monthlyPayment - interestPayment;
            balance -= principalPayment;
        }
        return { monthlyPayment, totalPaid, totalInterest, series };
    }, [principal, rate, years]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS" : "PARAMETERS"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Calculadora de préstamos" : "Loan calculator"}
                        </h3>
                    </div>
                    <SliderInput
                        label={language === "es" ? "Monto del préstamo" : "Loan amount"}
                        value={principal}
                        onChange={setPrincipal}
                        min={1000}
                        max={200000}
                        step={1}
                        format={fmt0}
                    />
                    <SliderInput
                        label={language === "es" ? "Tasa anual" : "Annual rate"}
                        value={rate}
                        onChange={setRate}
                        min={0.5}
                        max={30}
                        step={0.1}
                        unit="%"
                    />
                    <SliderInput
                        label={language === "es" ? "Plazo (años)" : "Term (years)"}
                        value={years}
                        onChange={setYears}
                        min={1}
                        max={30}
                        step={1}
                        unit={language === "es" ? " años" : " yrs"}
                    />
                    <div className="text-xs text-[hsl(var(--muted-foreground))] pt-2 border-t border-[hsl(var(--border))]">
                        {language === "es"
                            ? "Consejo: bajar la tasa 1% o acortar 2 años puede ahorrarte miles en intereses."
                            : "Tip: cutting the rate by 1% or 2 years off the term can save thousands in interest."}
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? "RESULTADO" : "RESULT"}
                    </div>
                    <ResultTile
                        label={language === "es" ? "Pago mensual" : "Monthly payment"}
                        numeric={result.monthlyPayment}
                        format={fmt}
                        color="text-[hsl(var(--primary))]"
                        big
                    />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile
                            label={language === "es" ? "Total pagado" : "Total paid"}
                            numeric={result.totalPaid}
                            format={fmt0}
                        />
                        <ResultTile
                            label={language === "es" ? "Intereses" : "Interest"}
                            numeric={result.totalInterest}
                            format={fmt0}
                            color="text-[#B85A3E] dark:text-[#F9C77E]"
                        />
                    </div>
                    <motion.div
                        key={`${principal}-${rate}-${years}`}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-40"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={result.series}>
                                <defs>
                                    <linearGradient id="loanBal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#7DD3B0" stopOpacity={0.55} />
                                        <stop offset="100%" stopColor="#7DD3B0" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 12)}y`} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                                <Tooltip formatter={(v) => fmt0(v)} labelFormatter={(m) => `Month ${m}`} />
                                <Area
                                    type="monotone"
                                    dataKey="balance"
                                    stroke="#7DD3B0"
                                    strokeWidth={2}
                                    fill="url(#loanBal)"
                                    isAnimationActive
                                    animationDuration={900}
                                    animationEasing="ease-out"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </motion.div>
                </CardContent>
            </Card>
        </div>
    );
};

// ---------- Investment Calculator ----------
const InvestmentCalc = ({ language }) => {
    const [initial, setInitial] = useState(5000);
    const [monthly, setMonthly] = useState(300);
    const [rate, setRate] = useState(8);
    const [years, setYears] = useState(20);

    const result = useMemo(() => {
        const monthlyRate = rate / 100 / 12;
        const n = years * 12;
        let balance = initial;
        let contributions = initial;
        const series = [{ year: 0, balance: Math.round(balance), contributions: Math.round(contributions) }];
        for (let i = 1; i <= n; i++) {
            balance = balance * (1 + monthlyRate) + monthly;
            contributions += monthly;
            if (i % 12 === 0) {
                series.push({ year: i / 12, balance: Math.round(balance), contributions: Math.round(contributions) });
            }
        }
        const gains = balance - contributions;
        return { future: balance, contributions, gains, series };
    }, [initial, monthly, rate, years]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS" : "PARAMETERS"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Calculadora de inversión" : "Investment calculator"}
                        </h3>
                    </div>
                    <SliderInput
                        label={language === "es" ? "Inversión inicial" : "Initial investment"}
                        value={initial}
                        onChange={setInitial}
                        min={0}
                        max={100000}
                        step={1}
                        format={fmt0}
                    />
                    <SliderInput
                        label={language === "es" ? "Aporte mensual" : "Monthly contribution"}
                        value={monthly}
                        onChange={setMonthly}
                        min={0}
                        max={5000}
                        step={1}
                        format={fmt0}
                    />
                    <SliderInput
                        label={language === "es" ? "Retorno anual" : "Annual return"}
                        value={rate}
                        onChange={setRate}
                        min={1}
                        max={20}
                        step={0.5}
                        unit="%"
                    />
                    <SliderInput
                        label={language === "es" ? "Horizonte (años)" : "Horizon (years)"}
                        value={years}
                        onChange={setYears}
                        min={1}
                        max={50}
                        step={1}
                        unit={language === "es" ? " años" : " yrs"}
                    />
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? "VALOR FUTURO" : "FUTURE VALUE"}
                    </div>
                    <ResultTile
                        label={language === "es" ? `En ${years} años` : `In ${years} years`}
                        numeric={result.future}
                        format={fmt0}
                        color="text-[hsl(var(--primary))]"
                        big
                    />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile
                            label={language === "es" ? "Aportado" : "Contributed"}
                            numeric={result.contributions}
                            format={fmt0}
                        />
                        <ResultTile
                            label={language === "es" ? "Ganancias" : "Gains"}
                            numeric={result.gains}
                            format={fmt0}
                            color="text-emerald-600"
                        />
                    </div>
                    <motion.div
                        key={`${initial}-${monthly}-${rate}-${years}`}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-48"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={result.series}>
                                <defs>
                                    <linearGradient id="invBal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#7DD3B0" stopOpacity={0.6} />
                                        <stop offset="100%" stopColor="#7DD3B0" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="invCon" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#B85A3E" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#B85A3E" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="hsl(var(--border))" />
                                <XAxis dataKey="year" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}y`} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                                <Tooltip formatter={(v) => fmt0(v)} />
                                <Area type="monotone" dataKey="contributions" stroke="#B85A3E" strokeWidth={2} fill="url(#invCon)" isAnimationActive animationDuration={900} animationEasing="ease-out" />
                                <Area type="monotone" dataKey="balance" stroke="#7DD3B0" strokeWidth={2.5} fill="url(#invBal)" isAnimationActive animationDuration={1000} animationEasing="ease-out" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </motion.div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-3">
                        {language === "es"
                            ? "El interés compuesto es tu mejor aliado. Aportes constantes + tiempo = crecimiento acelerado."
                            : "Compound interest is your best friend. Consistent deposits + time = accelerated growth."}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// ---------- Compound Interest ----------
const CompoundCalc = ({ language }) => {
    const [principal, setPrincipal] = useState(10000);
    const [rate, setRate] = useState(5);
    const [years, setYears] = useState(10);
    const [compoundPerYear, setCompoundPerYear] = useState(12);

    const result = useMemo(() => {
        const series = [];
        for (let y = 0; y <= years; y++) {
            const amount = principal * Math.pow(1 + rate / 100 / compoundPerYear, compoundPerYear * y);
            series.push({ year: y, amount: Math.round(amount) });
        }
        const finalAmount = series[series.length - 1].amount;
        return { finalAmount, interest: finalAmount - principal, series };
    }, [principal, rate, years, compoundPerYear]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS" : "PARAMETERS"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Interés compuesto" : "Compound interest"}
                        </h3>
                    </div>
                    <SliderInput label={language === "es" ? "Principal" : "Principal"} value={principal} onChange={setPrincipal} min={100} max={200000} step={1} format={fmt0} />
                    <SliderInput label={language === "es" ? "Tasa anual" : "Annual rate"} value={rate} onChange={setRate} min={0.1} max={20} step={0.1} unit="%" />
                    <SliderInput label={language === "es" ? "Plazo (años)" : "Term (years)"} value={years} onChange={setYears} min={1} max={50} step={1} unit={language === "es" ? " años" : " yrs"} />
                    <div>
                        <Label className="text-xs font-semibold tracking-[0.14em] text-[hsl(var(--muted-foreground))] uppercase mb-2 block">
                            {language === "es" ? "Capitalización" : "Compounding"}
                        </Label>
                        <div className="grid grid-cols-4 gap-2">
                            {[{ v: 1, l: language === "es" ? "Anual" : "Yearly" }, { v: 4, l: language === "es" ? "Trim." : "Qtr" }, { v: 12, l: language === "es" ? "Mensual" : "Monthly" }, { v: 365, l: language === "es" ? "Diaria" : "Daily" }].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setCompoundPerYear(o.v)}
                                    className={`pill text-xs font-semibold py-2 transition-colors ${compoundPerYear === o.v ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))]/60 text-[hsl(var(--foreground))]"}`}
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? "RESULTADO" : "RESULT"}
                    </div>
                    <ResultTile
                        label={language === "es" ? "Valor final" : "Final amount"}
                        numeric={result.finalAmount}
                        format={fmt0}
                        color="text-[hsl(var(--primary))]"
                        big
                    />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile label={language === "es" ? "Principal" : "Principal"} numeric={principal} format={fmt0} />
                        <ResultTile label={language === "es" ? "Interés ganado" : "Interest earned"} numeric={result.interest} format={fmt0} color="text-emerald-600" />
                    </div>
                    <motion.div
                        key={`${principal}-${rate}-${years}-${compoundPerYear}`}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-56"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={result.series}>
                                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="hsl(var(--border))" />
                                <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                                <Tooltip formatter={(v) => fmt0(v)} />
                                <Line type="monotone" dataKey="amount" stroke="#7DD3B0" strokeWidth={2.5} dot={false} isAnimationActive animationDuration={1000} animationEasing="ease-out" />
                            </LineChart>
                        </ResponsiveContainer>
                    </motion.div>
                </CardContent>
            </Card>
        </div>
    );
};

// ---------- Mortgage ----------
const MortgageCalc = ({ language }) => {
    const [price, setPrice] = useState(350000);
    const [down, setDown] = useState(20);
    const [rate, setRate] = useState(7);
    const [years, setYears] = useState(30);

    const result = useMemo(() => {
        const loan = price * (1 - down / 100);
        const monthlyRate = rate / 100 / 12;
        const n = years * 12;
        const monthlyPayment = monthlyRate === 0
            ? loan / n
            : (loan * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
        const totalPaid = monthlyPayment * n;
        const totalInterest = totalPaid - loan;
        const donutData = [
            { name: language === "es" ? "Principal" : "Principal", value: loan, color: "#7DD3B0" },
            { name: language === "es" ? "Intereses" : "Interest", value: totalInterest, color: "#B85A3E" },
        ];
        return { loan, monthlyPayment, totalPaid, totalInterest, donutData };
    }, [price, down, rate, years, language]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS" : "PARAMETERS"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Calculadora de hipoteca" : "Mortgage calculator"}
                        </h3>
                    </div>
                    <SliderInput label={language === "es" ? "Precio de la casa" : "Home price"} value={price} onChange={setPrice} min={50000} max={1500000} step={1} format={fmt0} />
                    <SliderInput label={language === "es" ? "Enganche" : "Down payment"} value={down} onChange={setDown} min={0} max={50} step={1} unit="%" />
                    <SliderInput label={language === "es" ? "Tasa anual" : "Annual rate"} value={rate} onChange={setRate} min={2} max={12} step={0.1} unit="%" />
                    <SliderInput label={language === "es" ? "Plazo" : "Term"} value={years} onChange={setYears} min={10} max={40} step={1} unit={language === "es" ? " años" : " yrs"} />
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? "PAGO MENSUAL" : "MONTHLY PAYMENT"}
                    </div>
                    <ResultTile
                        label={language === "es" ? `Con ${down}% de enganche` : `With ${down}% down`}
                        numeric={result.monthlyPayment}
                        format={fmt}
                        color="text-[hsl(var(--primary))]"
                        big
                    />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile label={language === "es" ? "Monto financiado" : "Loan amount"} numeric={result.loan} format={fmt0} />
                        <ResultTile label={language === "es" ? "Interés total" : "Total interest"} numeric={result.totalInterest} format={fmt0} color="text-[#B85A3E] dark:text-[#F9C77E]" />
                    </div>
                    <motion.div
                        key={`${price}-${down}-${rate}-${years}`}
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-48 flex items-center justify-center"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={result.donutData}
                                    dataKey="value"
                                    innerRadius="45%"
                                    outerRadius="80%"
                                    paddingAngle={2}
                                    isAnimationActive
                                    animationDuration={1000}
                                    animationEasing="ease-out"
                                >
                                    {result.donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Pie>
                                <Tooltip formatter={(v) => fmt0(v)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </motion.div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 text-center">
                        {language === "es" ? `Pagarás en total ${fmt0(result.totalPaid)} durante ${years} años.` : `Total paid over ${years} years: ${fmt0(result.totalPaid)}.`}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const Calculator = () => {
    const { language } = useApp();
    const [tab, setTab] = useState("loan");

    return (
        <AppLayout title={t(language, "nav_calculator")}>
            {/* Hero */}
            <div className="mb-6">
                <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase border-t border-[hsl(var(--border))] pt-1">
                    {language === "es" ? "HERRAMIENTAS · FINANCIERAS" : "TOOLS · FINANCIAL"}
                </div>
                <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] mt-2">
                    {language === "es" ? "Simula tu futuro financiero" : "Simulate your financial future"}
                </h1>
                <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-2xl">
                    {language === "es"
                        ? "Cuatro calculadoras esenciales con explicaciones claras y gráficos interactivos. Toma decisiones con datos, no con corazonadas."
                        : "Four essential calculators with clear explanations and interactive charts. Make decisions with data, not gut feelings."}
                </p>
            </div>

            {/* Tabs */}
            <div className="mb-6 flex flex-wrap gap-2" data-testid="calculator-tabs">
                {CALC_TABS.map((c) => {
                    const Icon = c.icon;
                    const active = tab === c.key;
                    return (
                        <button
                            key={c.key}
                            data-testid={`calculator-tab-${c.key}`}
                            onClick={() => setTab(c.key)}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 pill text-sm font-semibold transition-all ${
                                active
                                    ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] shadow-md"
                                    : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {language === "es" ? c.label_es : c.label_en}
                        </button>
                    );
                })}
            </div>

            {tab === "loan" && <motion.div key="loan" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}><LoanCalc language={language} /></motion.div>}
            {tab === "investment" && <motion.div key="investment" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}><InvestmentCalc language={language} /></motion.div>}
            {tab === "compound" && <motion.div key="compound" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}><CompoundCalc language={language} /></motion.div>}
            {tab === "mortgage" && <motion.div key="mortgage" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}><MortgageCalc language={language} /></motion.div>}
        </AppLayout>
    );
};

export default Calculator;
